import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Job {
  id: string;
  type: 'fal' | 'render';
  label: string;
  status: string;
  createdAt: string;
  error?: string | null;
  url?: string | null;
}

const DONE = ['done', 'completed', 'success'];
const FAIL = ['error', 'failed', 'crashed'];
const ACTIVE = ['queued', 'rendering', 'processing', 'submitted', 'pending', 'running'];

function badge(status: string) {
  const s = status.toLowerCase();
  if (DONE.includes(s)) return 'bg-emerald-900/50 text-emerald-300 border-emerald-700';
  if (FAIL.includes(s)) return 'bg-red-900/50 text-red-300 border-red-700';
  if (ACTIVE.includes(s)) return 'bg-amber-900/50 text-amber-300 border-amber-700';
  return 'bg-slate-800 text-slate-400 border-slate-700';
}

export function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const res = await api.get<{ jobs: Job[] }>('/jobs');
        if (!stop) {
          setJobs(res.jobs);
          setLoaded(true);
        }
      } catch {
        /* reintenta en el próximo tick */
      }
    };
    tick();
    const iv = setInterval(tick, 4000);
    return () => {
      stop = true;
      clearInterval(iv);
    };
  }, []);

  const active = jobs.filter((j) => ACTIVE.includes(j.status.toLowerCase())).length;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-slate-100">Trabajos</h1>
        {active > 0 && (
          <span className="text-xs text-amber-300">{active} en proceso…</span>
        )}
      </div>
      <p className="text-slate-400 text-sm mb-4">
        Generaciones (fal.ai) y renders de video tuyos. Se actualiza solo cada 4s.
      </p>

      {!loaded ? (
        <div className="text-slate-500 text-sm">Cargando…</div>
      ) : jobs.length === 0 ? (
        <div className="text-slate-600 text-sm">No hay trabajos recientes.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Tipo</th>
                <th className="text-left px-3 py-2 font-medium">Qué</th>
                <th className="text-left px-3 py-2 font-medium">Estado</th>
                <th className="text-left px-3 py-2 font-medium">Cuándo</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={`${j.type}-${j.id}`} className="border-t border-slate-800/70">
                  <td className="px-3 py-2">{j.type === 'render' ? '🎬 video' : '✨ fal'}</td>
                  <td className="px-3 py-2 text-slate-300">
                    {j.url ? (
                      <a href={j.url} target="_blank" rel="noreferrer" className="text-vision hover:underline">
                        {j.label}
                      </a>
                    ) : (
                      j.label
                    )}
                    {j.error && <div className="text-[11px] text-red-400 truncate max-w-[260px]">{j.error}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded border text-[11px] ${badge(j.status)}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs">
                    {new Date(j.createdAt).toLocaleString('es', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
